import { Link } from "react-router-dom";

import {
  actorEmail,
  allCapsOf,
  auditStamp,
  auditTarget,
  metricValue,
  blockedUserCount,
  eventsOf,
  metricPath,
  tenantsOf,
  type AuditPage,
  type FleetMetricEnvelope,
  type McpQuotaAll,
  type McpTenantList,
} from "../../api/mcp";
import { Blurred } from "../../components/Blurred";
import { Bars, MetricTile, series } from "../../components/charts";
import type { Resource } from "../../hooks/useResource";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Badge, Card, Notice, Page, Table, Td, Th } from "../../components/ui";
import { Button, Divider, Subheading, Text } from "../../f7";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { ProductCoverage } from "../../products/ProductCoverage";
import { ProductTag } from "../../products/PerProduct";

/**
 * Fleet posture — the design's layout, with every panel that has no endpoint
 * behind it rendered BLURRED rather than omitted.
 *
 * The design opens with four trend tiles and a billed-against-actual cost
 * chart. Two of those are Labeeb's and one needs a billing surface, so they
 * appear as shape only: the reader sees that a cost chart belongs here, cannot
 * read a cost, and is told which API would fill it. Omitting them would hide
 * what this console is going to be; filling them with plausible numbers is the
 * one thing it must never do.
 *
 * Everything else on this page is real: credits and admin actions today, the
 * tier distribution, the credit posture, the decision queue and the fleet-wide
 * audit tail.
 */
export function FleetPosture({ config }: { config: AppConfig }) {
  const tenants = useResource<McpTenantList>(config, "/mcp/admin/tenants");
  const quota = useResource<McpQuotaAll>(config, "/mcp/admin/quota");
  // Seven independent calls, not one bundled read: `GET /admin/fleet/metrics`
  // is gone (MCP split it into `GET /admin/metrics/{key}`, one metric per
  // call, no shared `generated_at` because there is no longer one moment that
  // produces all of them together). Each metric gets its own resource so one
  // failing tile does not blank the other six.
  const usageToday = useResource<FleetMetricEnvelope<"usage_today">>(
    config,
    metricPath("usage_today"),
  );
  const tierDistribution = useResource<FleetMetricEnvelope<"tier_distribution">>(
    config,
    metricPath("tier_distribution"),
  );
  const adminActionsToday = useResource<FleetMetricEnvelope<"admin_actions_today">>(
    config,
    metricPath("admin_actions_today"),
  );
  const tenantsCount = useResource<FleetMetricEnvelope<"tenants_count">>(
    config,
    metricPath("tenants_count"),
  );
  const topToolsToday = useResource<FleetMetricEnvelope<"top_tools_today">>(
    config,
    metricPath("top_tools_today"),
  );
  const quotaOverridesCount = useResource<FleetMetricEnvelope<"quota_overrides_count">>(
    config,
    metricPath("quota_overrides_count"),
  );
  // Fleet-wide, newest first. The design's "Recent writes · this session"
  // panel, except it survives a reload because there is a real audit route.
  const audit = useResource<AuditPage>(config, "/mcp/admin/audit");

  // Through `metricValue`, which folds "still loading", "failed" and "200
  // with no value" into the one answer a tile can act on: no number yet.
  const usage = metricValue(usageToday.state);
  const actionsToday = metricValue(adminActionsToday.state);
  const tenantCount = metricValue(tenantsCount.state);
  const q = quota.state.kind === "ready" ? quota.state.data : null;
  const s = tenants.state;

  return (
    <Page
      title="Fleet posture"
      subtitle={`${config.env === "prod" ? "Production" : "Development"} · where the fleet stands and what needs a decision.`}
    >
      <div className="flex flex-col gap-6">
        {/* WHOSE FLEET. Every figure below is MCP's — credits, admin actions,
            the tier distribution, the decision queue — and without saying so
            they read as the fleet's whole AI posture. They are half of it. */}
        <ProductCoverage config={config} />

        {/* ── The design's headline strip ─────────────────────────────── */}
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(185px,1fr))]">
          <MetricTile
            label="Credits today"
            aside={<ProductTag id="mcp" />}
            value={usage ? usage.total_credits.toLocaleString() : "…"}
            note="across the fleet"
            values={[]}
            noSeries="One UTC day — this route serves no history, so there is no trend to draw."
            from={usage ? usage.day : "—"}
            to="UTC"
          />

          <MetricTile
            label="Admin changes today"
            aside={<ProductTag id="mcp" />}
            value={actionsToday !== null ? actionsToday.toLocaleString() : "…"}
            note="through this console"
            values={[]}
            noSeries="A UTC calendar day, not a rolling 24 hours — it resets at midnight."
            from={usage ? usage.day : "—"}
            to="UTC"
          />

          <MetricTile
            label="Tenants"
            aside={<ProductTag id="mcp" />}
            value={tenantCount !== null ? tenantCount.toLocaleString() : "…"}
            note="in MCP's verification ledger"
            values={[]}
            noSeries="A count, not a rate — this route serves no history."
            from="—"
            to="now"
          />

          <Blurred what="Messages and profiled users" needs="Labeeb's admin API" minHeight={168}>
            <MetricTile
              label="Messages"
              value="—"
              note="across the fleet"
              values={series("placeholder-messages", 30, 55)}
              from="—"
              to="—"
            />
          </Blurred>

          <Blurred
            what="Computed cost, billed against actual"
            needs="a billing or warehouse surface"
            minHeight={168}
          >
            <MetricTile
              label="Computed cost"
              value="—"
              note="reported vs computed"
              values={series("placeholder-cost", 30, 13)}
              bad
              from="—"
              to="—"
            />
          </Blurred>
        </div>

        {/* ── Cost gap · tier distribution ─────────────────────────────
            `gap-4` between panels, which is the design's own figure. At gap-6
            the two columns read as separate rows of the page rather than a
            pair, and the row below then needed a third value to look even. */}
        <div className="grid gap-4 lg:grid-cols-2">
          <Blurred
            what="Cost gap — billed against actual, by month"
            needs="a billing or warehouse surface"
            minHeight={260}
          >
            {/* The design's cost-gap panel in full: a labelled y-axis, its
                gridlines, paired reported/computed bars and the legend footer.
                A bare row of bars left most of a stretched panel empty, and a
                placeholder that does not carry the panel's real shape defeats
                the point of drawing it at all. */}
            <Card title="Cost gap" note="Billed against actual · by month · USD">
              <div className="flex gap-2">
                <div className="relative w-8 shrink-0" style={{ height: "150px" }}>
                  {[400, 300, 200, 100, 0].map((v, i) => (
                    <span
                      key={v}
                      className="absolute right-0 font-mono text-[10px] leading-none tabular-nums text-slate-400 dark:text-slate-500"
                      style={{ top: `${i * 37.5}px`, transform: "translateY(-50%)" }}
                    >
                      ${v}
                    </span>
                  ))}
                </div>
                <div className="relative min-w-0 flex-1">
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 flex flex-col justify-between"
                    style={{ height: "150px" }}
                  >
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={
                          i === 4
                            ? "border-t border-slate-950/20 dark:border-white/20"
                            : "border-t border-slate-950/5 dark:border-white/10"
                        }
                      />
                    ))}
                  </div>
                  <div className="relative flex items-end gap-3">
                    {series("placeholder-costgap", 6, 40).map((v, i) => (
                      <div key={i} className="flex min-w-0 flex-1 flex-col items-center">
                        <div
                          className="flex w-full items-end justify-center gap-1"
                          style={{ height: "150px" }}
                        >
                          <div
                            className="w-[34%] rounded-t-sm bg-slate-300 dark:bg-slate-600"
                            style={{ height: `${35 + v}%` }}
                          />
                          <div
                            className="w-[34%] rounded-t-sm bg-brand-500"
                            style={{ height: `${45 + v}%` }}
                          />
                        </div>
                        <span className="mt-1.5 font-mono text-[10px] text-slate-400 dark:text-slate-500">
                          ———
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-slate-950/5 pt-3 text-[11px] dark:border-white/10">
                <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                  <span className="size-2 rounded-sm bg-slate-300 dark:bg-slate-600" />
                  Reported
                </span>
                <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                  <span className="size-2 rounded-sm bg-brand-500" />
                  Computed
                </span>
              </div>
            </Card>
          </Blurred>

          {/* ONE panel, as the design draws it: the tier distribution and the
              credit posture are the same question — how the fleet is spread,
              and whether its ceilings are set — and a Divider between them
              says "still the same panel" where a second card said "a new
              subject". */}
          <TierAndCredits
            state={tierDistribution.state}
            onRetry={tierDistribution.reload}
            quota={q}
          />
        </div>

        {/* ── Decision queue · recent writes ──────────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-2">
          <Card
            title="Needs a decision"
            note="Computed from the merchant list and the cap table · live"
            aside={
              <>
                {s.kind === "ready" && <DecisionCount tenants={s.data} quota={q} />}
                <ProductTag id="mcp" />
              </>
            }
          >
            {s.kind === "loading" && <Loading />}
            {s.kind === "signedout" && <SignedOut />}
            {s.kind === "denied" && <Denied message={s.error.message} />}
            {s.kind === "unavailable" && (
              <CouldNotLook
                what="MCP's merchants"
                message={s.error.message}
                onRetry={tenants.reload}
              />
            )}
            {(s.kind === "notfound" || s.kind === "error") && (
              <LoadFailed
                what="the decision queue"
                message={s.kind === "error" ? s.error.message : "Route not found."}
              />
            )}

            {s.kind === "ready" && <DecisionQueue tenants={s.data} quota={q} />}
          </Card>

          <RecentWrites audit={audit.state.kind === "ready" ? audit.state.data : null} />
        </div>

        {/* ── Top tools today · overrides per tier ─────────────────────── */}
        <div className="grid gap-4 lg:grid-cols-2">
          <TopToolsToday state={topToolsToday.state} onRetry={topToolsToday.reload} />
          <QuotaOverridesByTier
            state={quotaOverridesCount.state}
            onRetry={quotaOverridesCount.reload}
          />
        </div>
      </div>
    </Page>
  );
}

/** Which tools are actually being spent against, right now — not a config. */
function TopToolsToday({
  state,
  onRetry,
}: {
  state: Resource<FleetMetricEnvelope<"top_tools_today">>;
  onRetry: () => void;
}) {
  const rows = metricValue(state);

  return (
    <Card title="Top tools today" note="By credits spent · UTC day" aside={<ProductTag id="mcp" />}>
      {state.kind === "loading" && <Loading what="top tools" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="top tools" message={state.error.message} onRetry={onRetry} />
      )}
      {state.kind === "notfound" && <LoadFailed what="top tools" message="Route not found." />}
      {(state.kind === "invalid" || state.kind === "error") && (
        <LoadFailed what="top tools" message={state.error.message} />
      )}

      {state.kind === "ready" &&
        (rows && rows.length > 0 ? (
          <Bars
            rows={rows.map((r) => ({
              label: r.tool,
              value: r.credits,
              note: `${r.credits} credits`,
              tone: "brand" as const,
            }))}
            max={Math.max(...rows.map((r) => r.credits))}
          />
        ) : (
          <Notice title="No spend yet today">Nothing has been billed since UTC midnight.</Notice>
        ))}
    </Card>
  );
}

/** How many tools on each tier have their own row, rather than inheriting `*`. */
function QuotaOverridesByTier({
  state,
  onRetry,
}: {
  state: Resource<FleetMetricEnvelope<"quota_overrides_count">>;
  onRetry: () => void;
}) {
  const byTier = metricValue(state);
  const tiers = byTier ? Object.keys(byTier).sort() : [];

  return (
    <Card
      title="Cap overrides by tier"
      note="Tools with their own row, not inheriting the tier's * default"
      aside={<ProductTag id="mcp" />}
    >
      {state.kind === "loading" && <Loading what="cap overrides" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="cap overrides" message={state.error.message} onRetry={onRetry} />
      )}
      {state.kind === "notfound" && <LoadFailed what="cap overrides" message="Route not found." />}
      {(state.kind === "invalid" || state.kind === "error") && (
        <LoadFailed what="cap overrides" message={state.error.message} />
      )}

      {state.kind === "ready" &&
        (byTier && tiers.length > 0 ? (
          <Bars
            rows={tiers.map((t) => ({
              label: t,
              value: byTier[t] ?? 0,
              note: "own rows",
              tone: "brand" as const,
            }))}
            max={Math.max(...tiers.map((t) => byTier[t] ?? 0), 1)}
          />
        ) : (
          <Notice title="No overrides">Every tier resolves entirely through its * default.</Notice>
        ))}
    </Card>
  );
}

/**
 * Tier distribution and credit posture — one panel, the way the design draws
 * it, split by a Divider rather than by a card boundary.
 *
 * A FAILED FETCH IS NOT A SLOW ONE. This used to take the resolved value and
 * render `Loading…` whenever it was absent, so a 403 or a dead route showed
 * a spinner that never resolved — the operator waits, then reloads, then
 * files a bug against the wrong thing. Each state now says which it is.
 */
function TierAndCredits({
  state,
  onRetry,
  quota,
}: {
  state: Resource<FleetMetricEnvelope<"tier_distribution">>;
  onRetry: () => void;
  quota: McpQuotaAll | null;
}) {
  const distribution = metricValue(state);
  const total = distribution
    ? Object.values(distribution).reduce((n, v) => n + v, 0)
    : null;

  return (
    <Card
      title="Tenants by tier"
      note="MCP entitlements · configured records only"
      aside={
        <>
          {total !== null && (
            <span className="font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
              {total} measured
            </span>
          )}
          <ProductTag id="mcp" />
        </>
      }
    >
      {state.kind === "loading" && <Loading what="the tier distribution" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook
          what="the tier distribution"
          message={state.error.message}
          onRetry={onRetry}
        />
      )}
      {state.kind === "notfound" && (
        <LoadFailed what="the tier distribution" message="Route not found." />
      )}
      {(state.kind === "invalid" || state.kind === "error") && (
        <LoadFailed what="the tier distribution" message={state.error.message} />
      )}

      {state.kind === "ready" && distribution && (
        <>
          <Bars
            rows={Object.entries(distribution)
              .sort(([a], [b]) => (a === "none" ? 1 : b === "none" ? -1 : a.localeCompare(b)))
              .map(([tier, count]) => ({
                label: tier === "none" ? "no entitlements row" : tier,
                value: count,
                note: `of ${total}`,
                // "none" is not a tier — it is merchants the server treats as
                // free without a stored row, a different fact worth its own tone.
                tone: tier === "none" ? ("mute" as const) : ("brand" as const),
              }))}
            max={total ?? 1}
          />
          {/* The design's axis under the bars: without it the longest bar reads
              as "full" rather than as a count against the fleet. */}
          <div className="mt-2 flex justify-between font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
            <span>0</span>
            <span>{total}</span>
          </div>
        </>
      )}

      <Divider className="my-4" />

      <Subheading>Credit posture</Subheading>
      {(() => {
        const caps = allCapsOf(quota);
        const tiers = Object.keys(caps);
        const uncapped = tiers.filter((t) => (caps[t]?.["*"] ?? -1) < 0);
        return (
          <>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl/8 font-semibold tabular-nums text-slate-950 dark:text-white">
                {quota ? uncapped.length : "…"}
              </span>
              <span className="text-xs/5 text-slate-500 dark:text-slate-400">
                of {quota ? tiers.length : "…"} tiers uncapped
              </span>
            </div>
            {quota && (
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {uncapped.map((t) => (
                  <li key={t}>
                    <Badge tone="amber">{t}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </>
        );
      })()}
      {/* An outline Button, as the design draws it. A text link with an arrow
          was the one call-to-action on this page that did not look like one. */}
      <div className="mt-4">
        <Button outline href="/policy/tiers">
          Open tool caps
        </Button>
      </div>
    </Card>
  );
}

interface DecisionRow {
  label: string;
  sub: string;
  why: string;
  tone: "amber" | "red";
  to: string;
}

/**
 * What needs acting on, computed from the two lists the page already reads.
 *
 * Shared by the panel and the count in its header, so the badge can never
 * disagree with the rows beneath it.
 */
function decisionRows(tenants: McpTenantList, quota: McpQuotaAll | null): DecisionRow[] {
  void quota; // no longer a source of rows — see the removal note below
  const rows: DecisionRow[] = [];

  for (const t of tenantsOf(tenants)) {
    const whys: string[] = [];
    let tone: "amber" | "red" = "amber";
    if (t.blocked) {
      whys.push("blocked merchant-wide");
      tone = "red";
    }
    // Through the helper: dev has already dropped the names array for a count,
    // and reading `.length` off the absent field is what took this page down.
    const blockedHere = blockedUserCount(t);
    if (blockedHere) {
      whys.push(`${blockedHere} user${blockedHere === 1 ? "" : "s"} blocked here`);
    }
    if (whys.length) {
      rows.push({
        label: t.business_name,
        sub: t.business_reference,
        why: whys.join(" · "),
        tone,
        to: `/tenants/${encodeURIComponent(t.business_reference)}`,
      });
    }
  }
  for (const e of tenants.users_blocked_across_all_merchants ?? []) {
    rows.push({
      label: e,
      sub: "every merchant",
      why: "blocked fleet-wide — not attached to any one merchant",
      tone: "amber",
      to: "/policy/access",
    });
  }
  // `unprotected_resolvers` used to add a row per tier here. MCP removed the
  // field along with the resolver-cap guard itself (commit 0bf50a1) — there is
  // no live data left to compute this from. See RESOLVER_GUARD_REMOVED in
  // api/mcp.ts for where that fact is said instead: the cap editor, at the
  // moment someone is about to cap a search_* tool, not a queue item nobody
  // acts on because nothing changed since yesterday.
  return rows;
}

/** The design's "N open" badge, in the panel header where it puts it. */
function DecisionCount({
  tenants,
  quota,
}: {
  tenants: McpTenantList;
  quota: McpQuotaAll | null;
}) {
  const n = decisionRows(tenants, quota).length;
  return <Badge tone={n ? "amber" : "green"}>{n} open</Badge>;
}

/** Computed, never decorative — an empty queue means nothing to act on. */
function DecisionQueue({
  tenants,
  quota,
}: {
  tenants: McpTenantList;
  quota: McpQuotaAll | null;
}) {
  const rows = decisionRows(tenants, quota);

  if (!rows.length) {
    return (
      <Notice title="Nothing needs a decision">
        No blocks in force, and every resolver has an explicit cap row.
      </Notice>
    );
  }

  return (
    // `[--gutter:0px]` is the design's own setting for a table inside a card:
    // F7's Table indents its cells by a page gutter, which double-padded every
    // one of these and pushed the first column off the panel's own text edge.
    <Table
      className="[--gutter:0px]"
      head={
        <tr>
          <Th>Subject</Th>
          <Th>Why</Th>
        </tr>
      }
    >
      {rows.map((r, i) => (
        <tr key={i}>
          <Td>
            <Link to={r.to} className="font-medium text-brand-600 hover:underline dark:text-brand-300">
              <span dir="auto">{r.label}</span>
            </Link>
            <div className="ref text-xs text-slate-400 dark:text-slate-500">{r.sub}</div>
          </Td>
          {/* `max-w-xs`, the design's own width on this cell. Without it the
              longest reason ran past the panel's edge and was clipped by the
              card's overflow — the row that most needed reading was the one
              that lost its last words. */}
          <Td>
            <span
              className={`block max-w-xs whitespace-normal text-xs ${
                r.tone === "red"
                  ? "text-red-600 dark:text-red-400"
                  : "text-amber-700 dark:text-amber-400"
              }`}
            >
              {r.why}
            </span>
          </Td>
        </tr>
      ))}
    </Table>
  );
}

/** The design's "Recent writes", backed by the real fleet-wide audit route. */
function RecentWrites({ audit }: { audit: AuditPage | null }) {
  return (
    <Card
      title="Recent writes"
      note="Fleet-wide · newest first"
      aside={
        <Button plain href="/governance/audit">
          Full audit
        </Button>
      }
    >
      {!audit ? (
        <Loading what="the audit tail" />
      ) : eventsOf(audit).length === 0 ? (
        // The design's empty panel: a filled box that says what would land here
        // and offers the way to the log. A bare Notice said only "nothing yet".
        <div className="rounded-lg bg-slate-50 p-6 text-center ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10">
          <Text>No admin writes recorded yet.</Text>
          <p className="mt-1 text-xs/5 text-slate-500 dark:text-slate-400">
            Every change made here lands in the audit log with its read-back.
          </p>
          <div className="mt-3">
            <Button outline href="/governance/audit">
              Open audit log
            </Button>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-slate-950/5 dark:divide-white/10">
          {eventsOf(audit).slice(0, 6).map((e, i) => (
            <div key={e.request_id ?? i} className="flex items-start justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm/5 text-slate-950 dark:text-white">
                  <span className="ref">{e.action}</span> · {auditTarget(e.target) ?? "not recorded"}
                </p>
                {/* `auditStamp`, not `new Date(...)`: a row with no stamp gave
                    `new Date(null)` — the epoch — and rendered "1/1/1970" as
                    though that were when the change happened. */}
                <p className="truncate font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
                  {auditStamp(e.timestamp)} · {actorEmail(e.actor)}
                </p>
              </div>
              <Badge tone="neutral">{e.domain}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
